/**
 * Browser device identity for Gateway pairing.
 *
 * The Gateway identifies a paired device by an Ed25519 keypair: `device.id` must
 * be the lowercase hex SHA-256 of the raw 32-byte public key, `device.publicKey`
 * is that raw key base64url-encoded, and every connect signs the challenge nonce
 * (see `src/infra/device-identity-store.ts` and `ed25519-signature.ts` upstream).
 *
 * The private key is the one credential that never crosses the wire, so it stays
 * in this browser's localStorage and is only ever used to sign the handshake.
 */
import { getPublicKeyAsync, hashes, signAsync, utils } from "@noble/ed25519";

// @noble/ed25519 reaches for crypto.subtle for SHA-512, which browsers gate to
// secure contexts. A Gateway on a plain-HTTP LAN origin is a normal setup, so
// fall back to the pure-JS digest there instead of failing to pair at all.
const loadPureSha2 = () => import("@noble/hashes/sha2.js");
const subtleSha512Async = hashes.sha512Async;
hashes.sha512Async = async (message: Uint8Array) => {
  if (globalThis.crypto?.subtle && subtleSha512Async) {
    return await subtleSha512Async(message);
  }
  return Uint8Array.from((await loadPureSha2()).sha512(message));
};

const STORAGE_KEY = "openclaw-messenger:device-identity:v1";

export type DeviceIdentity = {
  deviceId: string;
  /** Raw 32-byte public key, base64url. */
  publicKey: string;
  /** Raw 32-byte secret key, base64url. */
  privateKey: string;
};

type StoredIdentity = DeviceIdentity & { version: 1 };

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function base64UrlDecode(value: string): Uint8Array {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function safeLocalStorage(): Storage | null {
  try {
    const probe = globalThis.localStorage;
    if (!probe) {
      return null;
    }
    // Private-browsing modes expose the object but throw on write.
    const key = "__openclaw_probe__";
    probe.setItem(key, "1");
    probe.removeItem(key);
    return probe;
  } catch {
    return null;
  }
}

/** `device.id` is the SHA-256 fingerprint of the raw public key, lowercase hex. */
async function fingerprintPublicKey(publicKey: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const digest = await subtle.digest("SHA-256", publicKey.slice().buffer as ArrayBuffer);
    return bytesToHex(new Uint8Array(digest));
  }
  return bytesToHex((await loadPureSha2()).sha256(publicKey));
}

async function generateIdentity(): Promise<DeviceIdentity> {
  const privateKey = utils.randomSecretKey();
  const publicKey = await getPublicKeyAsync(privateKey);
  return {
    deviceId: await fingerprintPublicKey(publicKey),
    publicKey: base64UrlEncode(publicKey),
    privateKey: base64UrlEncode(privateKey),
  };
}

// A storage-blocked page must still present one stable device for its lifetime:
// minting a new key per reconnect would raise a fresh pairing request every time
// and never hold an approval.
let ephemeralIdentity: DeviceIdentity | null = null;

export async function loadOrCreateDeviceIdentity(): Promise<DeviceIdentity> {
  const storage = safeLocalStorage();
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredIdentity;
      if (
        parsed?.version === 1 &&
        typeof parsed.deviceId === "string" &&
        typeof parsed.publicKey === "string" &&
        typeof parsed.privateKey === "string"
      ) {
        // Re-derive rather than trust the stored id: a mismatch would be rejected
        // by the Gateway as DEVICE_AUTH_DEVICE_ID_MISMATCH with no way to recover.
        const derivedId = await fingerprintPublicKey(base64UrlDecode(parsed.publicKey));
        if (derivedId !== parsed.deviceId) {
          const repaired: StoredIdentity = { ...parsed, deviceId: derivedId };
          storage?.setItem(STORAGE_KEY, JSON.stringify(repaired));
        }
        return {
          deviceId: derivedId,
          publicKey: parsed.publicKey,
          privateKey: parsed.privateKey,
        };
      }
    }
  } catch {
    // A corrupt record is replaced by a fresh identity below.
  }

  if (ephemeralIdentity) {
    return ephemeralIdentity;
  }

  const identity = await generateIdentity();
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify({ version: 1, ...identity } satisfies StoredIdentity));
  } catch {
    ephemeralIdentity = identity;
  }
  if (!storage) {
    ephemeralIdentity = identity;
  }
  return identity;
}

/** Signs one handshake payload with the stored device key. */
export async function signWithIdentity(
  identity: DeviceIdentity,
  payload: string,
): Promise<string> {
  const signature = await signAsync(
    new TextEncoder().encode(payload),
    base64UrlDecode(identity.privateKey),
  );
  return base64UrlEncode(signature);
}

/** Forgets this browser's device key; the next connect pairs as a new device. */
export function clearDeviceIdentity(): void {
  ephemeralIdentity = null;
  try {
    safeLocalStorage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
